/**
 * RowSelector (§2.3.2)
 *
 * Displays all rows of a working paper and lets the user select
 * which row(s) to bind to a pair endpoint. Supports single/multi-select
 * and marks rows that are already occupied by other pairs.
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, List, ListItemButton,
    ListItemIcon, ListItemText, Radio, Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import type {RowBinding} from '@/store/audit/adjustDraftStore';
import type {
    CommonInvestmentRowOption,
    ToPaperWpRowSelectionRow,
} from '@/wp/utils/initial-data-factories';

export interface RowSelectorProps {
    code: string;
    rows: any[];
    selectedRowBindings: RowBinding[];
    onSelect: (binding: RowBinding) => void;
    onDeselect: (binding: RowBinding) => void;
    multiple?: boolean;
    excludeRowKeys?: string[];
    /** Field name to display as row label (default: 'item') */
    labelField?: string;
    /** Field name for the adjustment value (default: 'adjustment') */
    adjustmentField?: string;
}

export interface RowSelectorDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (binding: RowBinding) => void;
    onCancel: () => void;
    code: string;
    rows: any[];
    title?: string;
    description?: string;
    labelField?: string;
    confirmLabel?: string;
    cancelLabel?: string;
}

/** Build a unique key for a row */
function buildRowKey(code: string, row: any, index: number): string {
    const id = row?.id;
    return `${code}:${id != null && id > 0 ? id : `idx_${index}`}`;
}

/** Build a RowBinding from a row and index */
function buildBinding(row: any, index: number): RowBinding {
    return {
        rowId: row?.id != null ? Number(row.id) : undefined,
        rowIndex: index,
        rowKey: row?.item || row?.directorName || `Row ${index + 1}`,
    };
}

/** Map CommonInvestmentRowOption to RowSelector row format */
export function mapCommonInvestmentOptionsToRows(options: CommonInvestmentRowOption[]) {
    return options.map((opt, index) => ({
        id: index,
        item: opt.label,
        category: opt.category,
        rowIndex: opt.rowIndex,
        optionId: opt.id,
    }));
}

/** Map toPaper WP row selection data to RowSelector row format */
export function mapToPaperSelectionRowsToRows(rows: ToPaperWpRowSelectionRow[]) {
    return rows.map((r, index) => ({
        id: index,
        item: r.rowKey,
        perClient: r.perClient,
        arrayName: r.arrayName,
        rowIndex: r.rowIndex,
    }));
}

/** Resolve CommonInvestmentRowOption from a RowBinding */
export function findCommonInvestmentOptionByBinding(
    options: CommonInvestmentRowOption[],
    binding: RowBinding,
): CommonInvestmentRowOption | undefined {
    const mappedRows = mapCommonInvestmentOptionsToRows(options);
    const selectedRow = mappedRows[binding.rowIndex ?? 0];
    return options.find(opt => opt.id === selectedRow?.optionId);
}

export default function RowSelector({
    code,
    rows,
    selectedRowBindings,
    onSelect,
    onDeselect,
    multiple = false,
    excludeRowKeys = [],
    labelField = 'item',
    adjustmentField = 'adjustment',
}: RowSelectorProps) {
    // Set of selected row keys for quick lookup
    const selectedKeys = useMemo(() => {
        const keys = new Set<string>();
        selectedRowBindings.forEach((b, _i) => {
            if (b.rowId != null) keys.add(`${code}:${b.rowId}`);
            if (b.rowIndex != null) keys.add(`${code}:idx_${b.rowIndex}`);
        });
        return keys;
    }, [selectedRowBindings, code]);

    const isSelected = useCallback((row: any, index: number) => {
        const key = buildRowKey(code, row, index);
        return selectedKeys.has(key);
    }, [selectedKeys, code]);

    const isExcluded = useCallback((row: any, index: number) => {
        const key = buildRowKey(code, row, index);
        return excludeRowKeys.includes(key);
    }, [excludeRowKeys, code]);

    const handleToggle = useCallback((row: any, index: number) => {
        const binding = buildBinding(row, index);
        if (isSelected(row, index)) {
            onDeselect(binding);
        } else {
            onSelect(binding);
        }
    }, [isSelected, onSelect, onDeselect]);

    if (!rows || rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{p: 1}}>
                No rows available for selection.
            </Typography>
        );
    }

    return (
        <List dense disablePadding>
            {rows.map((row, index) => {
                const selected = isSelected(row, index);
                const excluded = isExcluded(row, index);
                const label = row?.[labelField] || row?.directorName || `Row ${index + 1}`;
                const adj = Number(row?.[adjustmentField]) || 0;
                const hasLock = row?.adjustHeaderId != null && Number(row.adjustHeaderId) > 0;

                return (
                    <ListItemButton
                        key={buildRowKey(code, row, index)}
                        onClick={() => !excluded && handleToggle(row, index)}
                        disabled={excluded}
                        selected={selected}
                        dense
                        sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            opacity: excluded ? 0.5 : 1,
                        }}
                    >
                        <ListItemIcon sx={{minWidth: 36}}>
                            {multiple ? (
                                <Checkbox
                                    edge="start"
                                    checked={selected}
                                    disabled={excluded}
                                    size="small"
                                />
                            ) : (
                                <Radio
                                    edge="start"
                                    checked={selected}
                                    disabled={excluded}
                                    size="small"
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    <Typography variant="body2" noWrap>{label}</Typography>
                                    {hasLock && (
                                        <LockOutlinedIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
                                    )}
                                </Box>
                            }
                            secondary={adj !== 0 ? `Adj: ${adj.toLocaleString('en-US', {minimumFractionDigits: 2})}` : undefined}
                        />
                        {excluded && (
                            <Chip label="In use" size="small" variant="outlined" color="warning" sx={{ml: 1}}/>
                        )}
                    </ListItemButton>
                );
            })}
        </List>
    );
}

/** Dialog wrapper with internal selection state for row picking flows */
export function RowSelectorDialog({
    open,
    onClose,
    onConfirm,
    onCancel,
    code,
    rows,
    title = 'Select Row for Adjustment',
    description = 'Please select which row to apply the adjustment to:',
    labelField = 'item',
    confirmLabel = 'Confirm',
    cancelLabel = 'Use Default (First Row)',
}: RowSelectorDialogProps) {
    const [selectedBinding, setSelectedBinding] = useState<RowBinding | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedBinding(null);
        }
    }, [open]);

    const handleSelect = useCallback((binding: RowBinding) => {
        setSelectedBinding(binding);
    }, []);

    const handleDeselect = useCallback((_binding: RowBinding) => {
        setSelectedBinding(null);
    }, []);

    const handleConfirm = useCallback(() => {
        if (selectedBinding) {
            onConfirm(selectedBinding);
        }
    }, [selectedBinding, onConfirm]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {description}
                </Typography>
                <RowSelector
                    code={code}
                    rows={rows}
                    selectedRowBindings={selectedBinding ? [selectedBinding] : []}
                    onSelect={handleSelect}
                    onDeselect={handleDeselect}
                    multiple={false}
                    labelField={labelField}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>
                    {cancelLabel}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedBinding}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export {buildRowKey, buildBinding};
